import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import {
    FacetsResponse, MappedFacetObject, Bucket, Range, BucketMappeped, User, FacetOptions
} from '../types/facetsTypes';
const logger = useLogger('TeamSchedule -> facetMappers');

type FacetType = 'timeRange' | 'numberRange' | 'nested' | 'default';

const facetTypeMap = new Map<string, FacetType>([
    [ 'firstReplyHandleTimeSecsFacet', 'timeRange' ],
    [ 'totalHandleTimeFacet', 'timeRange' ],
    [ 'totalResolutionTimeSecsFacet', 'timeRange' ],
    [ 'waitTimeSecsFacet', 'timeRange' ],
    [ 'talkTimeSecsFacet', 'timeRange' ],
    [ 'workEffortSecsFacet', 'timeRange' ],
    [ 'totalPublicCommentsFacet', 'numberRange' ],
    [ 'totalReassignsFacet', 'numberRange' ],
    [ 'totalReopensFacet', 'numberRange' ],
    [ 'userTeamIdFacet', 'nested' ]
]);

const lastBucketAsGreaterThan = [
    'totalPublicCommentsFacet', 'totalReassignsFacet', 'totalReopensFacet'
];

const adjustLastRangeCount = (ranges: Range[], buckets: Bucket[]): Range[] => {
    const lastBucket = buckets[buckets.length - 1];
    if (lastBucket.count > 0 && ranges.length > 0) {
        ranges[ranges.length - 1].count += lastBucket.count;
    }
    return ranges;
};

const mapUsers = (buckets: User[] = []): FacetOptions[] => {
    return buckets.map((bucket) => ({
        id: bucket.userId as string,
        label: bucket.userFullName as string,
        count: bucket.count
    }));
};

const MappingStrategies: Record<FacetType, (buckets: Bucket[], facetKey: string) => BucketMappeped[]> = {
    timeRange: (buckets: Bucket[]) => {
        const ranges: Range[] = [];

        for (let i = 0; i < buckets.length - 1; i++) {
            const start = parseInt(String(buckets[i]._id), 10);
            const end = parseInt(String(buckets[i + 1]._id), 10);

            if (isNaN(start) || isNaN(end)) {
                throw new Error(
                    `Invalid bucket IDs: ${buckets[i]._id} or ${buckets[i + 1]._id}.`
                );
            }

            ranges.push({
                id: `${start}_${end}`,
                label: formatRangeFromSeconds(start, end),
                count: buckets[i].count,
                value: `${start}_${end}`,
                start,
                end
            });
        }
        return adjustLastRangeCount(ranges, buckets);
    },
    numberRange: (buckets: Bucket[], facetKey: string) => {
        const ranges: Range[] = [];
        for (let i = 0; i < buckets.length - 1; i++) {
            const start = parseInt(String(buckets[i]._id), 10);
            const end = parseInt(String(buckets[i + 1]._id), 10);

            if (isNaN(start) || isNaN(end)) {
                throw new Error(
                    `Invalid bucket IDs: ${buckets[i]._id} or ${buckets[i + 1]._id}.`
                );
            }

            let label = String(start);
            const isLastBucket = i === buckets.length - 2;
            if (isLastBucket && lastBucketAsGreaterThan.includes(facetKey)) label = `${start} and above`;

            ranges.push({
                id: `${start}_${end}`,
                label,
                count: buckets[i].count,
                value: `${start}_${end}`,
                start,
                end
            });
        }
        return adjustLastRangeCount(ranges, buckets);
    },
    nested: (buckets: Bucket[]) => {
        return buckets.map((bucket) => ({
            id: bucket.teamId as string,
            label: bucket.teamName as string,
            count: bucket.count,
            subBuckets: mapUsers(bucket.users)
        }));
    },
    default: (buckets: Bucket[]) =>
        buckets.map((bucket) => ({
            id: bucket._id,
            label: String(bucket._id),
            count: bucket.count
        }))
};

export function mapFacetResponse(
    response: FacetsResponse
): MappedFacetObject {
    if (!response?.data?.[0]?.facet) {
        throw new Error('Invalid response format');
    }

    const facetData = response.data[0].facet;
    const result: MappedFacetObject = {};

    Object.keys(facetData).forEach((facetKey) => {
        const buckets = facetData[facetKey]?.buckets || [];
        const facetType: FacetType = getFacetType(facetKey);
        const mapStrategy = MappingStrategies[facetType] || MappingStrategies.default;

        result[facetKey] = {
            facetName: facetKey,
            options: mapStrategy(buckets, facetKey)
        };
    });

    return result;
}

export function getFacetType(facetKey: string): FacetType {
    return facetTypeMap.get(facetKey) || 'default';
}

export function formatRangeFromSeconds(start: string | number, end: string | number): string {
    try {
        const startSeconds = parseSeconds(start);
        const endSeconds = parseSeconds(end);
        return `${formatTime(startSeconds)} - ${formatTime(endSeconds - 1)}`;
    } catch (e) {
        logger.error(e);
        return 'Invalid time range';
    }
}

export function parseSeconds(value: string | number): number {
    const seconds = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (isNaN(seconds) || seconds < 0) {
        throw new Error(`Invalid value: ${value}. Must be a non-negative number.`);
    }
    return seconds;
}

export function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
}